import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, ButtonGroup, Form, Nav } from 'react-bootstrap';
import DOMPurify from 'dompurify';

// What may come in by paste or from the HTML tab. The server cleans the message again before sending; this keeps
// anything that could run code out of the page while it is being edited.
const PURIFY = {
  FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'meta', 'link', 'base'],
  FORBID_ATTR: ['class', 'id']
};
const clean = (html) => DOMPurify.sanitize(html || '', PURIFY);

/** '' for an editor with no words or images left in it (it still holds markup such as <p><br></p>). */
const normalize = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent.trim() === '' && !div.querySelector('img') ? '' : html;
};

const TOOLS = [
  { label: <strong>B</strong>, title: 'Bold', cmd: 'bold' },
  { label: <em>I</em>, title: 'Italic', cmd: 'italic' },
  { label: <u>U</u>, title: 'Underline', cmd: 'underline' },
  { label: 'H', title: 'Heading', cmd: 'formatBlock', arg: '<h2>' },
  { label: '¶', title: 'Normal text', cmd: 'formatBlock', arg: '<p>' },
  { label: '•', title: 'Bulleted list', cmd: 'insertUnorderedList' },
  { label: '1.', title: 'Numbered list', cmd: 'insertOrderedList' }
];

/**
 * A formatted-message editor for broadcast emails: a visual tab where pasting from a web page, an email or a
 * document keeps its formatting, and an HTML tab to see or paste the HTML itself. value and onChange are HTML.
 * The ref offers insertText(text), which puts text (a {{placeholder}}) where the cursor is.
 */
const RichEmailEditor = forwardRef(function RichEmailEditor({ value, onChange }, ref) {
  const [mode, setMode] = useState('visual');
  const editorRef = useRef(null);
  const sourceRef = useRef(null);
  const lastHtml = useRef(null);
  const savedRange = useRef(null);

  // Show a value that came from outside (a cleared form, the HTML tab) without disturbing the cursor while typing.
  // The editor box is new each time the Visual tab opens, so it is always filled then.
  useEffect(() => {
    const el = editorRef.current;
    if (mode !== 'visual' || !el) return;
    if (el.dataset.filled === '1' && value === lastHtml.current) return;
    el.innerHTML = clean(value);
    el.dataset.filled = '1';
    lastHtml.current = value;
  }, [value, mode]);

  const emit = () => {
    const html = normalize(editorRef.current.innerHTML);
    lastHtml.current = html;
    onChange(html);
  };

  const rememberSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  /** Put the cursor back where it was in the editor (or at its end), so a toolbar or insert acts there. */
  const restoreSelection = () => {
    const el = editorRef.current;
    el.focus();
    const sel = window.getSelection();
    if (savedRange.current && el.contains(savedRange.current.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } else if (!el.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  const run = (cmd, arg) => {
    restoreSelection();
    document.execCommand(cmd, false, arg);
    rememberSelection();
    emit();
  };

  const addLink = () => {
    const url = window.prompt('Link address (https://…, or mailto:…)', 'https://');
    if (!url) return;
    if (!/^(https?:\/\/|mailto:)\S+$/i.test(url.trim())) {
      window.alert('Use an address starting with https://, http:// or mailto:');
      return;
    }
    run('createLink', url.trim());
  };

  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    if (html) {
      document.execCommand('insertHTML', false, clean(html));
    } else {
      document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
    }
    emit();
  };

  useImperativeHandle(ref, () => ({
    insertText(text) {
      if (mode === 'html') {
        const el = sourceRef.current;
        const start = el?.selectionStart ?? value.length;
        const end = el?.selectionEnd ?? value.length;
        onChange(value.slice(0, start) + text + value.slice(end));
        requestAnimationFrame(() => {
          el?.focus();
          el?.setSelectionRange(start + text.length, start + text.length);
        });
        return;
      }
      run('insertText', text);
    }
  }));

  return (
    <div>
      <Nav variant="tabs" activeKey={mode} onSelect={(k) => setMode(k)} className="mb-0">
        <Nav.Item><Nav.Link eventKey="visual">Visual</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="html">HTML</Nav.Link></Nav.Item>
      </Nav>
      {mode === 'visual' ? (
        <div className="border border-top-0 rounded-bottom">
          <div className="d-flex flex-wrap gap-2 p-2 border-bottom">
            <ButtonGroup size="sm">
              {TOOLS.map((t) => (
                <Button key={t.title} variant="outline-secondary" title={t.title}
                  onMouseDown={(e) => e.preventDefault()} onClick={() => run(t.cmd, t.arg)}>
                  {t.label}
                </Button>
              ))}
            </ButtonGroup>
            <ButtonGroup size="sm">
              <Button variant="outline-secondary" title="Add a link to the selected words"
                onMouseDown={(e) => e.preventDefault()} onClick={addLink}>Link</Button>
              <Button variant="outline-secondary" title="Remove the link"
                onMouseDown={(e) => e.preventDefault()} onClick={() => run('unlink')}>Unlink</Button>
              <Button variant="outline-secondary" title="Clear formatting from the selected words"
                onMouseDown={(e) => e.preventDefault()} onClick={() => run('removeFormat')}>Clear</Button>
            </ButtonGroup>
          </div>
          {/* Light like the email itself, whatever the app theme, so what you see is what is sent */}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Message"
            className="p-3"
            style={{ minHeight: 260, maxHeight: 520, overflowY: 'auto', background: '#ffffff', color: '#1f2937',
              fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 15, lineHeight: 1.6, outline: 'none' }}
            onInput={emit}
            onPaste={onPaste}
            onKeyUp={rememberSelection}
            onMouseUp={rememberSelection}
            onBlur={rememberSelection}
          />
        </div>
      ) : (
        <Form.Control
          as="textarea"
          ref={sourceRef}
          rows={14}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="rounded-0 rounded-bottom border-top-0"
          style={{ fontFamily: 'Consolas, Menlo, monospace', fontSize: 13 }}
          placeholder="<p>Hi {{firstName}},</p>"
          aria-label="Message HTML"
        />
      )}
    </div>
  );
});

export default RichEmailEditor;
